/**
 * useFeatureGate -- Resuelve si un feature_key esta habilitado para el business activo.
 * Consume GET /api/v1/businesses/{id}/features (PANEL-SUBS-FEATURES-001 T2).
 *
 * No es un hook React/Vue (panel es JS modular vanilla); el nombre se mantiene por
 * coherencia con la mision. Es una funcion async con cache TTL.
 *
 * Uso:
 *   const gate = await useFeatureGate(businessId, 'publicidad');
 *   if (gate.blocked) renderBanner(); else renderContent();
 *
 * Cache: por businessId con TTL de 60s (evita refetches al cambiar de tab dentro
 * de la misma vista). Llamar `invalidateFeatureGateCache(businessId)` tras un
 * cambio de plan o renovacion de suscripcion.
 *
 * @module presentation/hooks/useFeatureGate
 */

import { SubscriptionApiClient } from '../../infrastructure/api/SubscriptionApiClient.js';

const TTL_MS = 60 * 1000;
const _cache = new Map(); // businessId -> { ts, payload }

/**
 * @typedef {Object} FeatureGateResult
 * @property {boolean} loading                - true mientras hace fetch (siempre false al resolver)
 * @property {boolean} enabled                - feature explicitamente enabled=true en el plan activo
 * @property {boolean} blocked                - inverso de enabled (azucar para if checks)
 * @property {string}  reason                 - "no_business" | "no_subscription" | "feature_disabled" | "feature_not_in_plan" | "ok" | "error"
 * @property {string}  message                - mensaje legible para usuario
 * @property {Object|null} limit              - JSON parseado de limit_json si existe (ej. {max:3})
 * @property {number|null} daysToExpiry       - dias hasta end_date de la subscription (negativo si vencida)
 * @property {boolean} hasActiveSubscription  - flag del backend
 * @property {string|null} planCode           - plan normalizado activo (basico/plus/pro/pro_marketing/enterprise)
 */

/**
 * @param {string} businessId
 * @param {string} featureKey - desktop_installations | inventario_web | auditoria | publicidad | capacitacion | agentes_ia
 * @returns {Promise<FeatureGateResult>}
 */
export async function useFeatureGate(businessId, featureKey) {
  if (!businessId) {
    return _result({ enabled: false, reason: 'no_business', message: 'No hay negocio seleccionado' });
  }
  if (!featureKey) {
    return _result({ enabled: false, reason: 'error', message: 'feature_key requerido' });
  }

  const payload = await _fetchOrCache(businessId);
  if (!payload) {
    return _result({ enabled: false, reason: 'error', message: 'No se pudo consultar features (red/API)' });
  }

  if (!payload.hasActiveSubscription) {
    return _result({
      enabled: false,
      reason: 'no_subscription',
      message: payload.message || 'Sin suscripcion activa',
      hasActiveSubscription: false,
      planCode: null
    });
  }

  const features = Array.isArray(payload.features) ? payload.features : [];
  const found = features.find(f => f.featureKey === featureKey);
  const days = _calcDaysToExpiry(payload.subscriptionEndDate);

  if (!found) {
    return _result({
      enabled: false,
      reason: 'feature_not_in_plan',
      message: `La feature "${featureKey}" no esta incluida en tu plan ${payload.planRaw || payload.planCode || ''}`,
      hasActiveSubscription: true,
      planCode: payload.planCode || null,
      daysToExpiry: days
    });
  }

  if (!found.enabled) {
    return _result({
      enabled: false,
      reason: 'feature_disabled',
      message: `La feature "${featureKey}" no esta habilitada en tu plan actual`,
      hasActiveSubscription: true,
      planCode: payload.planCode || null,
      daysToExpiry: days
    });
  }

  return _result({
    enabled: true,
    reason: 'ok',
    message: 'Feature habilitada',
    limit: _parseLimit(found.limitJson),
    hasActiveSubscription: true,
    planCode: payload.planCode || null,
    daysToExpiry: days
  });
}

/**
 * Invalida el cache de un businessId (llamar tras cambio de plan o renovacion).
 * Sin argumento, limpia todo el cache.
 */
export function invalidateFeatureGateCache(businessId = null) {
  if (businessId == null) _cache.clear();
  else _cache.delete(businessId);
}

// ------------------ helpers internos ------------------

async function _fetchOrCache(businessId) {
  const hit = _cache.get(businessId);
  if (hit && (Date.now() - hit.ts) < TTL_MS) return hit.payload;

  const res = await SubscriptionApiClient.fetchBusinessFeatures(businessId);
  if (!res?.ok) return null;

  // ApiResponse<BusinessFeaturesResponse> -> el payload util esta en res.data.data
  // Compat: si el server devuelve directo el body (sin envelope), usar res.data.
  const payload = res.data?.data ?? res.data;
  _cache.set(businessId, { ts: Date.now(), payload });
  return payload;
}

function _calcDaysToExpiry(endDateStr) {
  if (!endDateStr) return null;
  const end = new Date(endDateStr);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function _parseLimit(limitJson) {
  if (!limitJson) return null;
  if (typeof limitJson === 'object') return limitJson;
  try { return JSON.parse(limitJson); } catch { return null; }
}

function _result(partial) {
  return {
    loading: false,
    enabled: false,
    blocked: true,
    reason: 'error',
    message: '',
    limit: null,
    daysToExpiry: null,
    hasActiveSubscription: false,
    planCode: null,
    ...partial,
    blocked: !(partial.enabled === true)
  };
}
